import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
    FiShoppingBag, FiLogOut, FiMenu, FiX,
    FiChevronsLeft, FiChevronsRight, FiFileText,
} from "react-icons/fi";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { cmLogout } from "../redux/cmAuthSlice";
import LogoutConfirmModal from "../components/common/LogoutConfirmModal";

export default function ContentManagerLayout() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleLogout = () => {
        dispatch(cmLogout());
        navigate("/content-manager/login");
    };

    const navItems = [
        { name: "Page Management", path: "/content-manager/page-management", icon: <FiShoppingBag /> },
        { name: "Content Requests", path: "/content-manager/content-requests", icon: <FiFileText /> },
    ];

    return (
        <div className="flex h-screen bg-gray-50 font-poppins overflow-hidden">
            {/* MOBILE OVERLAY */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* SIDEBAR */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 bg-white flex flex-col shadow-xl border-r border-gray-100 transition-all duration-300 ease-in-out 
                    md:relative md:translate-x-0 
                    ${isSidebarOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0"}
                    ${isCollapsed ? "md:w-20" : "md:w-64"}
                `}
            >
                {/* Sidebar Header */}
                <div className={`h-12 flex items-center ${isCollapsed ? "justify-center" : "justify-between px-4"} border-b border-gray-100 flex-shrink-0 transition-all duration-300`}>
                    {!isCollapsed && (
                        <div className="flex items-center gap-2">
                            <img
                                src="/logo.png"
                                alt="DECO TV Frames"
                                className="h-6 w-auto object-contain"
                            />
                            <span className="text-sm font-bold text-[#B08D55]">Content Manager</span>
                        </div>
                    )}
                    {/* Desktop Toggle Button */}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="hidden md:flex p-1.5 rounded-lg text-gray-400 hover:text-[#B08D55] hover:bg-[#B08D55]/10 transition-colors"
                    >
                        {isCollapsed ? <FiChevronsRight size={20} /> : <FiChevronsLeft size={20} />}
                    </button>

                    {/* Mobile Close Button */}
                    <div className="md:hidden absolute top-4 right-4">
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            className="p-2 text-gray-400 hover:text-[#B08D55] transition-colors"
                        >
                            <FiX size={24} />
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-hidden">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsSidebarOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-4 px-0 py-3 rounded-lg transition-all duration-300 group font-medium ${isActive
                                    ? "bg-[#B08D55]/10 text-[#B08D55]"
                                    : "text-gray-500 hover:bg-gray-50 hover:text-[#B08D55]"
                                } ${isCollapsed ? "justify-center" : "px-4"}`
                            }
                            title={isCollapsed ? item.name : ""}
                        >
                            <span className="text-xl flex-shrink-0">{item.icon}</span>
                            {!isCollapsed && <span className="whitespace-nowrap transition-opacity duration-300 opacity-100">{item.name}</span>}
                        </NavLink>
                    ))}
                </nav>

                {/* Footer */}
                <div className={`p-4 border-t border-gray-100 ${isCollapsed ? "flex justify-center" : ""}`}>
                    <button
                        onClick={() => setIsLogoutModalOpen(true)}
                        className={`flex items-center gap-3 px-4 py-3 text-gray-500 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors font-medium w-full ${isCollapsed ? "justify-center px-0" : ""}`}
                        title={isCollapsed ? "Logout" : ""}
                    >
                        <FiLogOut size={20} />
                        {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <main className="flex-1 h-full overflow-y-auto relative w-full bg-gray-50">
                {/* Mobile Toggle Button */}
                <div className="md:hidden p-4 sticky top-0 z-30 bg-white/90 backdrop-blur-sm border-b border-gray-200">
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="p-2 text-gray-600 hover:text-[#B08D55] rounded-lg transition-colors"
                    >
                        <FiMenu size={24} />
                    </button>
                </div>

                <div className="p-4 md:p-6">
                    <Outlet />
                </div>
            </main>

            <LogoutConfirmModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={handleLogout}
            />
        </div>
    );
}
